import { PERMISSION_CODES, type PermissionCode } from './permissions.ts';

/**
 * Seeded roles (CLAUDE.md §7) with their DEFAULT permissions. Each (role, permission) default is
 * applied to the database once; if the owner later removes it, it is never added back.
 */
export type RoleScope = 'ALL' | 'LOCATION';

export interface RoleDefinition {
  readonly code: string;
  readonly nameAr: string;
  readonly nameEn: string;
  /** How assignments of this role are normally scoped (the admin UI uses it as the default). */
  readonly defaultScope: RoleScope;
  /** Management roles must use an authenticator app (CLAUDE.md §3.2.5). */
  readonly requires2fa: boolean;
  readonly permissions: readonly PermissionCode[];
}

const everything = PERMISSION_CODES;

export const ROLES = [
  {
    code: 'partner',
    nameAr: 'شريك',
    nameEn: 'Partner',
    defaultScope: 'ALL',
    requires2fa: true,
    // Sees everything and approves; Emergency Mode is activated by IT, partners end it.
    permissions: everything.filter((p) => p !== 'emergency.activate'),
  },
  {
    code: 'general_manager',
    nameAr: 'المدير العام',
    nameEn: 'General Manager',
    defaultScope: 'ALL',
    requires2fa: true,
    permissions: [
      'location.view',
      'settings.view',
      'settings.operations.edit',
      'user.view',
      'user.create',
      'user.update',
      'user.suspend',
      'user.assign_roles',
      'role.view',
      'device.view',
      'printer.manage',
      'session.view_all',
      'audit.view',
      'approval.view',
      'ledger.view',
      'cashbox.view',
      'sync.issue.view',
      'sync.issue.resolve',
      'notification.view_log',
      // Phase 2 (plan §18): sees the warehouse, its costs and every statement; keeps the count lists.
      'inventory.view',
      'inventory.cost.view',
      'supplier.view',
      'supplier.statement.view',
      'purchase.view',
      'supply.account.view',
      'count.configure',
    ],
  },
  {
    code: 'it_admin',
    nameAr: 'مسؤول تقنية المعلومات',
    nameEn: 'IT Admin',
    defaultScope: 'ALL',
    requires2fa: true,
    permissions: [
      'location.view',
      'location.manage',
      'settings.view',
      'settings.operations.edit',
      'settings.security.edit',
      'user.view',
      'user.create',
      'user.update',
      'user.suspend',
      'user.reset_credentials',
      'user.assign_roles',
      'role.view',
      'role.manage',
      'device.view',
      'device.enroll',
      'device.revoke',
      'hub.connection.manage',
      'printer.manage',
      'session.view_all',
      'session.revoke_any',
      'audit.view',
      'audit.verify',
      'emergency.activate',
      'sync.issue.view',
      'sync.issue.resolve',
      'notification.view_log',
      'notification.send_test',
      'backup.view',
    ],
  },
  {
    code: 'accountant',
    nameAr: 'المحاسب',
    nameEn: 'Accountant',
    defaultScope: 'ALL',
    requires2fa: true,
    permissions: [
      'location.view',
      'settings.view',
      'settings.finance.edit',
      'approval.view',
      'audit.view',
      'ledger.view',
      'ledger.period.close',
      'ledger.correction.request',
      'cashbox.view',
      'cashbox.manage',
      'cashbox.transfer.send',
      'cashbox.transfer.receive',
      'cashbox.transfer.dispute',
      'sync.issue.view',
      'sync.issue.resolve',
      // D74: approves how branch managers resolve financial sync issues.
      'sync.issue.approve',
      // Phase 2 (plan §6, §18): pays suppliers and receives franchise payments from / into the box
      // they hold (the main safe), approves write-offs; never records invoices or receives goods.
      'inventory.view',
      'inventory.cost.view',
      'inventory.import',
      'inventory.opening.prepare',
      'inventory.correction.request',
      'supplier.view',
      'supplier.manage',
      'supplier.statement.view',
      'purchase.view',
      'supplier.payment.record',
      'supply.incident.approve',
      'supply.account.view',
      'supply.payment.receive',
    ],
  },
  {
    code: 'warehouse_manager',
    nameAr: 'مدير المخزن',
    nameEn: 'Warehouse Manager',
    defaultScope: 'LOCATION',
    requires2fa: true,
    permissions: [
      'location.view',
      'device.view',
      'sync.issue.view',
      'sync.issue.resolve',
      // Phase 2 (plan §18): runs the warehouse; records invoices but never pays them.
      'inventory.view',
      'inventory.cost.view',
      'inventory.item.manage',
      'inventory.price.manage',
      'inventory.waste.record',
      'inventory.fefo.override',
      'inventory.import',
      'inventory.opening.prepare',
      'inventory.correction.request',
      'supplier.view',
      'supplier.manage',
      'supplier.statement.view',
      'purchase.view',
      'purchase.record',
      'purchase.receive',
      'purchase.discrepancy.resolve',
      'supplier.return.record',
      'production.recipe.manage',
      'production.record',
      'supply.request.approve',
      'supply.shipment.dispatch',
      'supply.incident.resolve',
      'supply.account.view',
      'count.perform',
      'count.approve',
      'count.configure',
    ],
  },
  {
    code: 'warehouse_staff',
    nameAr: 'موظف مخزن',
    nameEn: 'Warehouse Staff',
    defaultScope: 'LOCATION',
    requires2fa: false,
    permissions: [
      'location.view',
      'sync.issue.view',
      // Phase 2 (plan §6.5, §18): no money, no costs — permanent and daily-wage workers alike.
      'inventory.view',
      'inventory.waste.record',
      'purchase.receive',
      'production.record',
      'supply.shipment.dispatch',
      'count.perform',
    ],
  },
  {
    code: 'hr_manager',
    nameAr: 'مدير الموارد البشرية',
    nameEn: 'HR Manager',
    defaultScope: 'ALL',
    requires2fa: true,
    permissions: [
      'location.view',
      'user.view',
      'user.create',
      'user.update',
      'user.suspend',
      'role.view',
    ],
  },
  {
    code: 'quality_manager',
    nameAr: 'مدير الجودة',
    nameEn: 'Quality Manager',
    defaultScope: 'ALL',
    requires2fa: true,
    permissions: ['location.view', 'inventory.view'],
  },
  {
    code: 'audit_manager',
    nameAr: 'مدير التدقيق',
    nameEn: 'Audit Manager',
    defaultScope: 'ALL',
    requires2fa: true,
    permissions: [
      'location.view',
      'audit.view',
      'audit.verify',
      'ledger.view',
      'cashbox.view',
      'device.view',
      // Phase 2 (plan §18): reads the warehouse for audits.
      'inventory.view',
      'inventory.cost.view',
      'supplier.view',
      'supplier.statement.view',
      'purchase.view',
      'supply.account.view',
    ],
  },
  {
    code: 'branch_manager',
    nameAr: 'مدير الفرع',
    nameEn: 'Branch Manager',
    defaultScope: 'LOCATION',
    requires2fa: true,
    permissions: [
      'location.view',
      'user.view',
      'device.view',
      'hub.connection.manage',
      'printer.manage',
      'approval.view',
      'cashbox.view',
      'cashbox.transfer.send',
      'cashbox.transfer.receive',
      'cashbox.transfer.dispute',
      'sync.issue.view',
      'sync.issue.resolve',
      // Phase 2 (plan §9, §10, §18): requests, receives and counts for the branch.
      'inventory.view',
      'inventory.waste.record',
      'supply.request.create',
      'supply.shipment.receive',
      'supply.account.view',
      'supply.return.create',
      'count.perform',
      'count.approve',
    ],
  },
  {
    // Phase 2 (owner A9, plan §18): «مخوَّل طلبات الفرع» — orders, receives and counts for the
    // branch on the manager's behalf; no money and no 2FA.
    code: 'branch_supply_delegate',
    nameAr: 'مخوَّل طلبات الفرع',
    nameEn: 'Branch supply delegate',
    defaultScope: 'LOCATION',
    requires2fa: false,
    permissions: [
      'location.view',
      'inventory.view',
      'inventory.waste.record',
      'supply.request.create',
      'supply.shipment.receive',
      'count.perform',
    ],
  },
  {
    code: 'shift_supervisor',
    nameAr: 'مشرف الوردية',
    nameEn: 'Shift Supervisor',
    defaultScope: 'LOCATION',
    requires2fa: false,
    permissions: [
      'location.view',
      'cashbox.view',
      'cashbox.transfer.send',
      'cashbox.transfer.receive',
      'sync.issue.view',
    ],
  },
  {
    code: 'cashier',
    nameAr: 'كاشير',
    nameEn: 'Cashier',
    defaultScope: 'LOCATION',
    requires2fa: false,
    permissions: ['cashbox.transfer.send'],
  },
  {
    code: 'call_center_agent',
    nameAr: 'موظف مركز الاتصال',
    nameEn: 'Call Center Agent',
    defaultScope: 'LOCATION',
    requires2fa: false,
    permissions: ['location.view'],
  },
  {
    code: 'driver',
    nameAr: 'سائق توصيل',
    nameEn: 'Driver',
    defaultScope: 'LOCATION',
    requires2fa: false,
    permissions: ['cashbox.transfer.send'],
  },
  {
    code: 'employee',
    nameAr: 'موظف',
    nameEn: 'Employee',
    defaultScope: 'LOCATION',
    requires2fa: false,
    // Self-service (own schedule, leave, payslips…) is implicit for every signed-in user.
    permissions: [],
  },
] as const satisfies readonly RoleDefinition[];

export type RoleCode = (typeof ROLES)[number]['code'];

/**
 * Offline allow-list defaults (plan §7.4, owner decision O3). An offline session gets only these
 * permissions, and only on a device at a location of the given type. Editable by the owner.
 */
export interface OfflinePolicy {
  readonly role: RoleCode;
  readonly locationType: 'BRANCH' | 'WAREHOUSE';
  readonly permissions: readonly PermissionCode[];
}

export const OFFLINE_POLICY_DEFAULTS = [
  {
    role: 'branch_manager',
    locationType: 'BRANCH',
    permissions: [
      'cashbox.transfer.send',
      'cashbox.transfer.receive',
      'cashbox.transfer.dispute',
      'sync.issue.view',
      'hub.connection.manage',
      'printer.manage',
      'inventory.view',
      'inventory.waste.record',
      'supply.request.create',
      'supply.shipment.receive',
      'supply.return.create',
      'count.perform',
    ],
  },
  // O27 (M7-5): IT may sign in on a branch PC without internet only to fix its connection to the
  // hub — and (M7-6) its printers. Branch PCs only: the warehouse's single PC is its own hub (D73).
  {
    role: 'it_admin',
    locationType: 'BRANCH',
    permissions: ['hub.connection.manage', 'printer.manage'],
  },
  {
    role: 'shift_supervisor',
    locationType: 'BRANCH',
    permissions: ['cashbox.transfer.send', 'cashbox.transfer.receive', 'sync.issue.view'],
  },
  { role: 'cashier', locationType: 'BRANCH', permissions: ['cashbox.transfer.send'] },
  // Phase 2 (plan §18): the warehouse's daily work goes on without internet (O3 extended).
  {
    role: 'warehouse_manager',
    locationType: 'WAREHOUSE',
    permissions: [
      'sync.issue.view',
      'inventory.view',
      'inventory.waste.record',
      'inventory.fefo.override',
      'purchase.record',
      'purchase.receive',
      'supplier.return.record',
      'production.record',
      'supply.request.approve',
      'supply.shipment.dispatch',
      'count.perform',
    ],
  },
  {
    role: 'warehouse_staff',
    locationType: 'WAREHOUSE',
    permissions: [
      'sync.issue.view',
      'inventory.view',
      'inventory.waste.record',
      'purchase.receive',
      'production.record',
      'supply.shipment.dispatch',
      'count.perform',
    ],
  },
  // Accountant on warehouse devices ONLY: cash handovers (O3), and in Phase 2 paying suppliers and
  // receiving franchise payments at the main safe (plan §18; expenses come with Phase 3).
  {
    role: 'accountant',
    locationType: 'WAREHOUSE',
    permissions: [
      'cashbox.transfer.send',
      'cashbox.transfer.receive',
      'cashbox.transfer.dispute',
      'supplier.payment.record',
      'supply.payment.receive',
    ],
  },
  {
    role: 'branch_supply_delegate',
    locationType: 'BRANCH',
    permissions: [
      'inventory.view',
      'inventory.waste.record',
      'supply.request.create',
      'supply.shipment.receive',
      'count.perform',
    ],
  },
] as const satisfies readonly OfflinePolicy[];
